import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Filter } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AdminLayout } from './AdminLayout';
import { QuoteForm } from './QuoteForm';
import { quotesAPI } from '../../services/api';
import { toast } from 'sonner';

export const QuotesManager = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await quotesAPI.getAll(statusFilter);
      setQuotes(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (quoteData) => {
    try {
      await quotesAPI.create(quoteData);
      setIsFormOpen(false);
      toast.success('Devis créé avec succès');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateQuote = async (quoteData) => {
    try {
      await quotesAPI.update(editingQuote.id, quoteData);
      setEditingQuote(null);
      setIsFormOpen(false);
      toast.success('Devis modifié avec succès');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteQuote = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        await quotesAPI.delete(id);
        toast.success('Devis supprimé');
        fetchQuotes();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await quotesAPI.updateStatus(id, newStatus);
      toast.success('Statut mis à jour');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingQuote(null);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.service.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      accepted: 'bg-green-100 text-green-700',
      pending: 'bg-orange-100 text-orange-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      accepted: 'Accepté',
      pending: 'En attente',
      rejected: 'Refusé'
    };
    return (
      <span className={`px-3 py-1 text-xs font-medium rounded-full ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Devis</h1>
            <p className="text-gray-600">Créez et gérez vos devis clients</p>
          </div>
          <Button
            onClick={() => setIsFormOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nouveau devis
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6 bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Rechercher par client, entreprise ou service..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-white">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="accepted">Acceptés</SelectItem>
                    <SelectItem value="rejected">Refusés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Client</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Entreprise</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Service</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Montant</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Statut</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4 text-gray-900">{quote.clientName}</td>
                      <td className="py-4 px-4 text-gray-600">{quote.company}</td>
                      <td className="py-4 px-4 text-gray-600">{quote.service}</td>
                      <td className="py-4 px-4 font-semibold text-gray-900">
                        {(quote.amount / 1000000).toFixed(2)}M FCFA
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {new Date(quote.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-4 px-4">
                        <Select
                          value={quote.status}
                          onValueChange={(value) => handleStatusChange(quote.id, value)}
                        >
                          <SelectTrigger className="w-32 border-none p-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                                En attente
                              </span>
                            </SelectItem>
                            <SelectItem value="accepted">
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                Accepté
                              </span>
                            </SelectItem>
                            <SelectItem value="rejected">
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Refusé
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(quote)}
                            className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredQuotes.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucun devis trouvé</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quote Form Modal */}
      {isFormOpen && (
        <QuoteForm
          quote={editingQuote}
          onSave={editingQuote ? handleUpdateQuote : handleCreateQuote}
          onCancel={handleCloseForm}
        />
      )}
    </AdminLayout>
  );
};